import { useEffect, useState } from 'react'
import { useStore } from '@/store'
import {
  aggWindowForTick,
  formatWindowBounds,
  parseAcePlayerTimestamp,
} from '@/analysis/tickTimestamp'
import { apiClient } from '@/analysis/apiClient'
import type { WindowTraceResult } from '@/analysis/windowTraceTypes'

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 180,
  height: 28,
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  padding: '0 10px',
  borderRadius: 6,
  border: '0.5px solid var(--bd-1)',
  background: 'var(--bg-3)',
  color: 'var(--t-0)',
  outline: 'none',
}

/**
 * Paste an AcePlayer tick `ts` → pin dashboard queries to that 10s agg window
 * so Hex/Deep show the slice that captured the tick (even a single client).
 */
export default function TickTimestampFilter({ compact = false }: { compact?: boolean }) {
  const setHistoricRange = useStore((s) => s.setHistoricRange)
  const setTimeMode = useStore((s) => s.setTimeMode)
  const window = useStore((s) => s.window)
  const historicFromTs = useStore((s) => s.historicFromTs)
  const historicTs = useStore((s) => s.historicTs)
  const timeMode = useStore((s) => s.timeMode)

  const active =
    timeMode === 'historic' &&
    window === 'custom' &&
    historicFromTs != null &&
    historicTs != null

  const drillStream = useStore((s) => s.drillStream)

  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [trace, setTrace] = useState<WindowTraceResult | null>(null)
  const [tracing, setTracing] = useState(false)

  useEffect(() => {
    if (active && historicFromTs != null) {
      // Prefer showing the tick-ish midpoint / window start for re-edit.
      setDraft(String(historicFromTs))
    }
  }, [active, historicFromTs])

  const apply = () => {
    const ts = parseAcePlayerTimestamp(draft)
    if (ts == null) {
      setError('Enter AcePlayer tick ts (unix ms) or an ISO datetime')
      return
    }
    const { fromMs, toMs } = aggWindowForTick(ts)
    setHistoricRange(fromMs, toMs)
    setError(null)

    // Pinning the range is not enough on its own: when the window produced no
    // rows the dashboard just renders empty, which is indistinguishable from a
    // broken filter. Ask the trace endpoint which stage the tick reached.
    setTracing(true)
    setTrace(null)
    apiClient
      .windowTrace({ tickTs: ts, streamId: drillStream ?? null })
      .then(setTrace)
      .catch((e) => setError(`Trace unavailable: ${String(e?.message ?? e)}`))
      .finally(() => setTracing(false))
  }

  const clear = () => {
    setDraft('')
    setError(null)
    setTrace(null)
    setTimeMode('live')
  }

  const jumpTo = (ms: number) => {
    const { fromMs, toMs } = aggWindowForTick(ms)
    setHistoricRange(fromMs, toMs)
    setDraft(String(fromMs))
    setTracing(true)
    setTrace(null)
    apiClient
      .windowTrace({ tickTs: ms, streamId: drillStream ?? null })
      .then(setTrace)
      .catch((e) => setError(`Trace unavailable: ${String(e?.message ?? e)}`))
      .finally(() => setTracing(false))
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 4 : 8,
        width: '100%',
      }}
    >
      {!compact && (
        <div style={{ fontSize: 11, color: 'var(--t-1)', lineHeight: 1.35 }}>
          Paste an AcePlayer tick <code style={{ fontFamily: 'var(--font-mono)' }}>ts</code> to
          filter Hex/Deep to the matching 10s aggregation window — including single-client slices —
          so you can compare player metrics to the dashboard average for that stream/window.
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value)
            setError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') apply()
          }}
          placeholder="AcePlayer tick ts (e.g. 1700000005234)"
          aria-label="AcePlayer tick timestamp"
          style={inputStyle}
        />
        <button
          type="button"
          className="chip on"
          style={{
            fontSize: 10,
            cursor: 'pointer',
            border: 'none',
            background: 'var(--info-bg)',
            color: 'var(--info)',
          }}
          onClick={apply}
        >
          Filter window
        </button>
        {active && (
          <button
            type="button"
            className="chip"
            style={{ fontSize: 10, cursor: 'pointer' }}
            onClick={clear}
          >
            Clear time
          </button>
        )}
      </div>
      {error && (
        <div style={{ fontSize: 10, color: 'var(--crit)' }}>{error}</div>
      )}
      {active && historicFromTs != null && historicTs != null && (
        <div
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            color: 'var(--info)',
          }}
          title="Dashboard queries use this exact aggregation window"
        >
          Active window · {formatWindowBounds(historicFromTs, historicTs)}
          <span style={{ opacity: 0.75 }}> · startMs={historicFromTs}</span>
        </div>
      )}
      {tracing && (
        <div style={{ fontSize: 10, color: 'var(--t-1)' }}>Tracing tick through pipeline…</div>
      )}
      {trace && <TraceVerdict trace={trace} onJump={jumpTo} />}
    </div>
  )
}

const VERDICT_COLOR: Record<WindowTraceResult['status'], string> = {
  found: 'var(--ok, var(--info))',
  window_not_emitted: 'var(--warn)',
  no_trace: 'var(--crit)',
}

const VERDICT_LABEL: Record<WindowTraceResult['status'], string> = {
  found: 'Window aggregated',
  window_not_emitted: 'Window never aggregated',
  no_trace: 'No trace',
}

/**
 * Turns "the dashboard is empty" into a statement about which stage the tick
 * reached, so a blank Hex/Deep can be told apart from a broken filter.
 */
function TraceVerdict({
  trace,
  onJump,
}: {
  trace: WindowTraceResult
  onJump: (ms: number) => void
}) {
  const snapshots = trace.runs.reduce((n, r) => n + r.snapshots.length, 0)
  const stats = trace.runs[0]?.stage1FilterStats ?? null
  const { beforeMs, afterMs } = trace.nearestEmittedWindowMs

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '6px 8px',
        borderRadius: 6,
        border: '0.5px solid var(--bd-1)',
        background: 'var(--bg-3)',
        fontSize: 10,
        lineHeight: 1.4,
      }}
    >
      <div style={{ color: VERDICT_COLOR[trace.status], fontWeight: 600 }}>
        {VERDICT_LABEL[trace.status]}
      </div>

      <div style={{ color: 'var(--t-1)', fontFamily: 'var(--font-mono)' }}>
        runs {trace.runs.length} · stream rows {snapshots} · raw ticks in window{' '}
        {trace.rawTicksInWindow.length}
      </div>

      {stats && (
        <div
          style={{ color: 'var(--t-1)', fontFamily: 'var(--font-mono)' }}
          title="Stage-1 filters: ticks discarded before aggregation"
        >
          stage1 in {stats.in} → out {stats.out} · idle {stats.droppedIdle} · hidden{' '}
          {stats.droppedHidden} · invalid {stats.droppedInvalid}
        </div>
      )}

      <div style={{ color: 'var(--t-2)' }}>{trace.diagnosis}</div>

      {trace.status !== 'found' && (beforeMs != null || afterMs != null) && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: 'var(--t-2)' }}>Jump to nearest:</span>
          {beforeMs != null && (
            <button
              type="button"
              className="chip"
              style={{ fontSize: 10, cursor: 'pointer' }}
              onClick={() => onJump(beforeMs)}
            >
              ← earlier
            </button>
          )}
          {afterMs != null && (
            <button
              type="button"
              className="chip"
              style={{ fontSize: 10, cursor: 'pointer' }}
              onClick={() => onJump(afterMs)}
            >
              later →
            </button>
          )}
        </div>
      )}
    </div>
  )
}
